import React from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import './BrandBillingDetail.scss';

import BrandBillingStripeIdField from './BrandBillingStripeIdField';
import BrandBillingNumberField from './BrandBillingNumberField';
import OpportunityStrategyBillingCard from './OpportunityStrategyBillingCard';
import { lookUpStripeObject, updateBrandBilling } from '../../api/brands';
import {
  STRIPE_DASHBOARD,
  COLLECTION_OPTIONS,
  PAST_DUE_REMINDER_OPTIONS,
  formatMrr,
  formatPaymentMethod,
  formatDollars,
  formatStripeDate,
  formatBillingDate
} from '../../helpers/brand_billing_helpers';

const INVOICE_TONES = { uncollectible: 'urgent' };

const BrandBillingDetail = props => {
  const { overview, isSaving } = props;
  const { brand, subscription, paymentMethods, commissions, opportunities, brandBudget, spotlights } = overview;

  // A subscription charges the method Stripe pinned on it, so that is the one this page reports. Any other
  // chargeable method on the customer is only a fallback, and is labelled as one rather than as what gets charged.
  const chargeableMethods = (paymentMethods.all || []).filter(pm => ['card', 'us_bank_account'].includes(pm.type));
  const pinnedMethod = chargeableMethods.find(pm => pm.id === subscription.defaultPaymentMethod);
  const method = pinnedMethod || chargeableMethods[0];
  const methodLabel = formatPaymentMethod(method);
  // Only a brand holding both kinds has a choice to make; with one, the preference can never apply
  const canChooseSource =
    (paymentMethods.all || []).some(pm => pm.type === 'card') && (paymentMethods.all || []).some(pm => pm.type === 'us_bank_account');
  const autoCharges = subscription.collectionMethod === 'charge_automatically';
  const isLive = !!subscription.stripeSubscriptionId;
  const strandedWithACard = isLive && !autoCharges && !!method;
  const invoice = subscription.latestInvoice;

  let paymentMethodSummary;
  if (!autoCharges) paymentMethodSummary = methodLabel ? `${methodLabel} on file` : 'no card on file';
  else if (pinnedMethod) paymentMethodSummary = methodLabel;
  else if (methodLabel) paymentMethodSummary = `no method pinned · ${methodLabel} on file`;
  else paymentMethodSummary = 'no payment method pinned';

  const collectionOption = COLLECTION_OPTIONS.find(option => option.value === overview.commissionInvoiceCollection);
  const reminderOption = PAST_DUE_REMINDER_OPTIONS.find(option => option.value === !!overview.disableInAppPastDueInvoiceReminders);
  const secondaryFlows = [
    ['Prepaid Opportunities', [['Net terms', opportunities.oppsNetPaymentTerms]]],
    ['Budget invoices', [['Allowed overage', brandBudget.allowedBudgetOverage, { edits: 'allowedBudgetOverage', prefix: '$' }], ['Overdue allowance', brandBudget.overdueBrandBudgetInvoiceAllowance]]], // prettier-ignore
    ['Spotlights', [['Payment method', spotlights.spotlightsStripePaymentMethodId || 'none'], ['Net terms', spotlights.spotlightNetPaymentTerms], ['Billing paused', spotlights.billingPaused ? 'yes' : 'no']], '/spotlights'] // prettier-ignore
  ];

  const saveStripeId = async updates => {
    await updateBrandBilling(brand.id, updates);
    await props.onReload();
  };

  return (
    <div className='brand-billing-detail'>
      <button className='brand-billing-back' onClick={props.onBack}>
        ← All brands
      </button>

      <header className='brand-billing-brand'>
        <h1 className='brand-billing-brand-name'>{brand.name}</h1>
        <div className='brand-billing-brand-domain'>
          {brand.domain} · Brand {brand.id}
        </div>
      </header>

      <div className='brand-billing-flows'>
        <section className={cn('brand-billing-card', { urgent: strandedWithACard || subscription.stripeError })}>
          <details className='brand-billing-disclosure' open>
            <summary className='brand-billing-summary'>
              <div className='brand-billing-summary-copy'>
                <div className='brand-billing-card-heading'>
                  <div className='brand-billing-summary-heading'>
                    <h2>Subscription</h2>
                    <span className={cn('brand-billing-status', { warning: subscription.stripeError })}>
                      {subscription.stripeError ? 'Unavailable' : subscription.status || 'Not connected'}
                    </span>
                  </div>
                  {!!brand.stripeCustomerId && (
                    <a
                      className='brand-billing-link'
                      href={`${STRIPE_DASHBOARD}/customers/${brand.stripeCustomerId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Manage in Stripe ↗
                    </a>
                  )}
                </div>
                <div className='brand-billing-summary-description'>
                  {subscription.stripeError
                    ? 'Could not read the subscription from Stripe'
                    : !isLive
                    ? 'No live subscription'
                    : autoCharges
                    ? 'Charged automatically'
                    : 'Invoiced, net-30'}
                  {!!isLive && !subscription.stripeError && <> · {paymentMethodSummary}</>}
                </div>
              </div>
              <FontAwesomeIcon className='brand-billing-chevron' icon={faChevronRight} />
            </summary>
            <div className='brand-billing-card-body'>
              <div className='brand-billing-metrics'>
                <div>
                  <span>Monthly recurring revenue</span>
                  <strong>{formatMrr(subscription.mrr)}</strong>
                </div>
                <div>
                  <span>Next bill</span>
                  <strong>{formatStripeDate(subscription.currentPeriodEnd) || '—'}</strong>
                </div>
                <div>
                  <span>Last invoice</span>
                  {invoice ? (
                    <a href={invoice.hosted_invoice_url || `${STRIPE_DASHBOARD}/invoices/${invoice.id}`} target='_blank' rel='noopener noreferrer'>
                      {formatDollars(invoice.total)} ·{' '}
                      <span className={cn('brand-billing-pill', INVOICE_TONES[invoice.status])}>{invoice.status}</span>
                    </a>
                  ) : (
                    <strong>—</strong>
                  )}
                </div>
                <div>
                  <span>Live since</span>
                  <strong>{formatBillingDate(subscription.startedAt) || '—'}</strong>
                </div>
                {!!subscription.cancelAt && (
                  <div>
                    <span>Cancels</span>
                    <strong>{formatStripeDate(subscription.cancelAt)}</strong>
                  </div>
                )}
              </div>
              <details className='brand-billing-connection'>
                <summary>
                  Edit Stripe customer & subscription IDs <FontAwesomeIcon icon={faChevronRight} />
                </summary>
                <div className='brand-billing-ids'>
                  <BrandBillingStripeIdField
                    label='Stripe customer'
                    value={brand.stripeCustomerId}
                    placeholder='cus_…'
                    linkUrl={`${STRIPE_DASHBOARD}/customers/${brand.stripeCustomerId}`}
                    onPreview={customerId => lookUpStripeObject('customer', customerId, brand.id)}
                    onSave={stripeCustomerId => saveStripeId({ stripeCustomerId })}
                    describePreview={preview =>
                      `${preview.customer.name || 'Unnamed'} · ${preview.customer.email || 'no email'}${
                        preview.claimedBy && preview.claimedBy.id !== brand.id
                          ? `\n\n${preview.claimedBy.name} (${preview.claimedBy.id}) already points at it.`
                          : ''
                      }`
                    }
                  />
                  <BrandBillingStripeIdField
                    label='Stripe subscription'
                    value={subscription.stripeSubscriptionId}
                    placeholder='sub_…'
                    linkUrl={`${STRIPE_DASHBOARD}/subscriptions/${subscription.stripeSubscriptionId}`}
                    onPreview={subscriptionId => lookUpStripeObject('subscription', subscriptionId, brand.id)}
                    onSave={stripeSubscriptionId => saveStripeId({ stripeSubscriptionId })}
                    describePreview={preview =>
                      `${preview.subscription.status} · ${preview.subscription.collection_method}${
                        preview.belongsToThisCustomer ? '' : '\n\nThis subscription belongs to a different Stripe customer.'
                      }`
                    }
                  />
                </div>
              </details>
            </div>
          </details>
          {!!strandedWithACard && (
            <div className='brand-billing-callout'>Has a payment method on file and is still being invoiced. Changing that is a Stripe change.</div>
          )}
        </section>

        <section className={cn('brand-billing-card', { urgent: overview.commissionInvoiceCollection === 'do_not_invoice' })}>
          <details className='brand-billing-disclosure' open>
            <summary className='brand-billing-summary'>
              <div className='brand-billing-summary-copy'>
                <div className='brand-billing-summary-heading'>
                  <h2>Commissions</h2>
                  {overview.commissionInvoiceCollection === 'do_not_invoice' && <span className='brand-billing-status warning'>Invoicing off</span>}
                </div>
                <div className='brand-billing-summary-description'>{collectionOption?.label} · Collection, payment terms & overdue allowances</div>
              </div>
              <FontAwesomeIcon className='brand-billing-chevron' icon={faChevronRight} />
            </summary>
            <div className='brand-billing-card-body'>
              <div className='brand-billing-segmented'>
                {COLLECTION_OPTIONS.map(option => (
                  <button
                    className={cn('brand-billing-segment', {
                      selected: overview.commissionInvoiceCollection === option.value,
                      destructive: option.value === 'do_not_invoice'
                    })}
                    key={option.value}
                    aria-pressed={overview.commissionInvoiceCollection === option.value}
                    disabled={isSaving}
                    onClick={() => props.onChangeCollection(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className='brand-billing-section-note'>{collectionOption?.note} Collection changes save immediately.</div>
              <div className='brand-billing-facts'>
                <div className='brand-billing-fact'>
                  <span>Net terms</span>
                  <span>{commissions.netPaymentTerms ?? '—'}</span>
                </div>
                <div className='brand-billing-fact'>
                  <span>Past-due alert</span>
                  <span>{commissions.daysUntilInvoicePastDueToAlert ?? '—'} days</span>
                </div>
                <div className='brand-billing-fact'>
                  <span>Overdue allowance</span>
                  <span>{commissions.overdueAffiliateInvoiceAllowance ?? '—'}</span>
                </div>
                <div className='brand-billing-fact'>
                  <span>Preferred source</span>
                  {canChooseSource ? (
                    <select
                      className='brand-billing-source'
                      aria-label='Preferred commission payment source'
                      value={commissions.autoChargeSource || ''}
                      disabled={isSaving}
                      onChange={e => props.onChangeSource(e.target.value || null)}
                    >
                      <option value=''>most recent</option>
                      <option value='card'>card</option>
                      <option value='ach'>bank</option>
                    </select>
                  ) : (
                    <span>{commissions.autoChargeSource || 'most recent'}</span>
                  )}
                </div>
              </div>
            </div>
          </details>
        </section>

        <section className='brand-billing-card'>
          <div className='brand-billing-card-heading'>
            <div className='brand-billing-summary-heading'>
              <h2>Past-due reminders</h2>
              {reminderOption.value && <span className='brand-billing-status warning'>Silenced</span>}
            </div>
            <label className='brand-billing-reminders-control'>
              <input
                type='checkbox'
                checked={!reminderOption.value}
                disabled={isSaving}
                onChange={e => props.onChangePastDueReminders(!e.target.checked)}
              />
              Remind in-app
            </label>
          </div>
          <div className='brand-billing-card-body brand-billing-section-note'>{reminderOption.note}</div>
        </section>

        <OpportunityStrategyBillingCard brand={brand} />

        <section className='brand-billing-card'>
          <details className='brand-billing-disclosure' open>
            <summary className='brand-billing-summary'>
              <div className='brand-billing-card-heading'>
                <h2>Payment methods</h2>
                {!!brand.stripeCustomerId && (
                  <a
                    className='brand-billing-link'
                    href={`${STRIPE_DASHBOARD}/customers/${brand.stripeCustomerId}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Manage in Stripe ↗
                  </a>
                )}
              </div>
              <FontAwesomeIcon className='brand-billing-chevron' icon={faChevronRight} />
            </summary>
            <div className='brand-billing-card-body'>
              <div className='brand-billing-methods'>
                {(paymentMethods.all || []).map(pm => (
                  <div className='brand-billing-method' key={pm.id}>
                    {formatPaymentMethod(pm)}
                    {paymentMethods.default?.id === pm.id && <span className='brand-billing-status'>Customer default</span>}
                  </div>
                ))}
                {!paymentMethods.all?.length && <span className='brand-billing-section-note'>No card or bank account on file.</span>}
              </div>
              <div className='brand-billing-section-note'>Each billing type can use a different payment method.</div>
            </div>
          </details>
        </section>
        <section className='brand-billing-card'>
          <details className='brand-billing-disclosure' open>
            <summary className='brand-billing-summary'>
              <div className='brand-billing-summary-copy'>
                <div className='brand-billing-summary-heading'>
                  <h2>Other billing</h2>
                  {!!spotlights.billingPaused && <span className='brand-billing-status warning'>Spotlights paused</span>}
                </div>
                <div className='brand-billing-summary-description'>Prepaid Opportunities, budget invoices & Spotlights</div>
              </div>
              <FontAwesomeIcon className='brand-billing-chevron' icon={faChevronRight} />
            </summary>
            <div className='brand-billing-card-body'>
              {secondaryFlows.map(([title, rows, editedAt]) => (
                <div className='brand-billing-panel' key={title}>
                  <div className='brand-billing-panel-title'>
                    {title}
                    {!!editedAt && (
                      <a href={editedAt} target='_blank' rel='noopener noreferrer'>
                        edit
                      </a>
                    )}
                  </div>
                  {rows.map(([label, value, editable], idx) => (
                    <div className='brand-billing-pair' key={`${title}-${label}-${idx}`}>
                      <span>{label}</span>
                      {editable ? (
                        <BrandBillingNumberField
                          label={label}
                          value={value}
                          prefix={editable.prefix}
                          onSave={next => props.onSaveField({ [editable.edits]: next })}
                        />
                      ) : (
                        <span>{value === null || value === undefined || value === '' ? '—' : String(value)}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </details>
        </section>
      </div>
    </div>
  );
};

export default BrandBillingDetail;
